import { useParams, Link, useNavigate } from "react-router-dom";
import { Calendar, Clock, Phone, ArrowLeft, ArrowRight } from "lucide-react";
import NavBar from "@/components/site/NavBar";
import Footer from "@/components/site/Footer";
import StickyCallButton from "@/components/site/StickyCallButton";
import useSEO from "@/hooks/useSEO";
import { getBlogPost, blogPostsList } from "@/data/blog";
import { buildBlogPostJsonLd, SITE_URL } from "@/data/structuredData";

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getBlogPost(slug);

  useSEO({
    title: post?.metaTitle,
    description: post?.metaDescription,
    canonical: post ? `${SITE_URL}/blog/${post.slug}` : undefined,
    jsonLd: post ? buildBlogPostJsonLd(post) : undefined,
  });

  if (!post) {
    return (
      <div className="min-h-screen bg-[#F4F4F5]">
        <NavBar />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display uppercase text-4xl mb-4">Article not found</h1>
          <button
            onClick={() => navigate("/blog")}
            className="inline-flex items-center gap-2 bg-[#D9480F] text-white px-5 py-3 font-display uppercase tracking-tight border-2 border-black"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const otherPosts = blogPostsList.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <NavBar />

      {/* Breadcrumbs */}
      <div className="border-b border-zinc-900 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600">
          <Link to="/" className="hover:text-[#D9480F]">Home</Link>
          <span>/</span>
          <Link to="/blog" className="hover:text-[#D9480F]">Blog</Link>
          <span>/</span>
          <span className="text-[#D9480F] font-bold truncate">{post.category}</span>
        </div>
      </div>

      <article className="border-b border-zinc-900 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-14 md:py-20">
          <div className="flex items-center gap-3 mb-6">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-[#D9480F] bg-[#F4F4F5] border border-zinc-300 px-2 py-1">
              {post.category}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {new Date(post.date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {post.readMinutes} min read
            </span>
          </div>

          <h1 className="font-display uppercase font-semibold leading-[0.95] tracking-tighter text-3xl md:text-5xl mb-8">
            {post.title}
          </h1>

          <div className="prose-content space-y-6">
            {post.content.map((block, i) => {
              if (block.type === "h2") {
                return (
                  <h2
                    key={i}
                    className="font-display uppercase text-2xl md:text-3xl font-semibold tracking-tight leading-tight text-[#0B2B40] pt-4"
                  >
                    {block.text}
                  </h2>
                );
              }
              if (block.type === "p") {
                return (
                  <p key={i} className="text-zinc-800 leading-relaxed text-base md:text-lg">
                    {block.text}
                  </p>
                );
              }
              if (block.type === "ul") {
                return (
                  <ul key={i} className="space-y-2 border-l-2 border-[#D9480F] pl-5">
                    {block.items.map((item, j) => (
                      <li key={j} className="text-zinc-800 leading-relaxed text-base md:text-lg">
                        {item}
                      </li>
                    ))}
                  </ul>
                );
              }
              return null;
            })}
          </div>

          {/* Inline CTA */}
          <div className="mt-14 bg-[#0B2B40] text-white border-2 border-black p-6 md:p-8 hard-shadow-orange">
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#F59E0B] font-bold mb-2">
              Need Help?
            </div>
            <h3 className="font-display uppercase text-2xl md:text-3xl tracking-tight mb-4">
              Talk to a licensed Pittsburgh pro.
            </h3>
            <p className="text-zinc-200 mb-6 leading-relaxed">
              Free inspection. Written quote. No pressure. Serving Pittsburgh and Allegheny County.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="tel:4847643392"
                className="inline-flex items-center gap-2 bg-[#D9480F] text-white px-5 py-3 font-display uppercase tracking-tight text-sm border-2 border-black"
              >
                <Phone className="w-4 h-4" strokeWidth={2.5} />
                484-764-3392
              </a>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 bg-transparent text-white border-2 border-white px-5 py-3 font-display uppercase tracking-tight text-sm hover:bg-white hover:text-black"
              >
                Book Inspection
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related posts */}
      {otherPosts.length > 0 && (
        <section className="bg-[#F4F4F5]">
          <div className="max-w-4xl mx-auto px-6 md:px-12 py-14 md:py-16">
            <h3 className="font-display uppercase text-2xl md:text-3xl tracking-tight mb-8">
              More from the journal
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {otherPosts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="bg-white border-2 border-black p-5 group hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[4px_4px_0_0_#D9480F] transition-all"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-[#D9480F] mb-2">
                    {p.category}
                  </div>
                  <h4 className="font-display uppercase text-lg tracking-tight leading-tight group-hover:text-[#D9480F]">
                    {p.title}
                  </h4>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default BlogPostPage;
